import { apiSlice } from './api-slice';
import {
  Response,
  Sortable,
  Task,
  PaginatedTask,
  NewTask,
  TaskEvent,
  TaskInsights,
  DetailedTask,
  TaskDocument,
  TaskComment,
  NewTaskComment,
  TaskExport,
  ExportTask,
} from '../models';
import { PAGINATION_SIZE } from '../constants';

export const tasksApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAllMyTasksList: builder.query<Response<Sortable<Task>>, PaginatedTask>({
      query: body => ({
        url: `company/taskBuilding/taskList`,
        method: 'POST',
        body: { size: PAGINATION_SIZE, ...body },
      }),
      providesTags: result =>
        result?.data.content.length
          ? result.data.content.map(({ id }) => ({ type: 'Task', id }))
          : ['Task'],
    }),
    exportAllMyTasksList: builder.mutation<Response<TaskExport>, ExportTask>({
      query: data => ({
        url: `company/taskBuilding/taskList/export`,
        method: 'POST',
        body: data,
      }),
    }),
    getAllTeamTasksList: builder.query<Response<Sortable<Task>>, PaginatedTask>(
      {
        query: body => ({
          url: `company/taskBuilding/teamTaskList`,
          method: 'POST',
          body: { size: PAGINATION_SIZE, ...body },
        }),
        providesTags: result =>
          result?.data.content.length
            ? result.data.content.map(({ id }) => ({ type: 'Task', id }))
            : ['Task'],
      }
    ),
    getTaskById: builder.query<Response<DetailedTask>, number>({
      query: workflowId => ({
        url: `company/taskBuilding/taskDetails/${Number(workflowId)}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'Task', id }],
    }),
    createTask: builder.mutation<Response<Task>, NewTask>({
      query: data => ({
        url: 'company/taskBuilding/task',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Task' }],
    }),
    submitTask: builder.mutation<
      Response<Task>,
      {
        taskId: number;
        formAnswers: NewTask['formAnswers'];
      }
    >({
      query: data => ({
        url: `company/taskBuilding/submitTask`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'Task' }],
    }),
    rejectTask: builder.mutation<
      Response<Task>,
      {
        taskId: number;
      }
    >({
      query: data => ({
        url: `company/taskBuilding/rejectTask`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'Task' }],
    }),
    getTaskEventLogs: builder.query<Response<Sortable<TaskEvent>>, number>({
      query: taskId => ({
        url: `company/taskBuilding/taskEventLogs/${taskId}`,
        method: 'GET',
        params: { size: 1000, page: 0 },
      }),
      providesTags: (_, __, id) => [{ type: 'Task', id }],
    }),
    getTaskDocuments: builder.query<Response<TaskDocument[]>, number>({
      query: taskId => ({
        url: `company/taskBuilding/taskDocuments/${taskId}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'Task', id }],
    }),
    uploadTaskDocuments: builder.mutation<Response<Document[]>, FormData>({
      query: data => ({
        url: `company/taskBuilding/taskDocuments`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'Task' }],
    }),
    getTaskThreads: builder.query<Response<TaskComment[]>, number>({
      query: taskId => ({
        url: `company/threadsBuilding/comments/${taskId}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'Task', id }],
    }),
    createTaskComment: builder.mutation<Response<TaskComment>, NewTaskComment>({
      query: data => ({
        url: `company/threadsBuilding/comment`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { taskId }) => [{ type: 'Task', id: taskId }],
    }),
    getMyTaskInsights: builder.query<
      Response<TaskInsights>,
      { phaseId?: number; projectId?: number }
    >({
      query: params => ({
        url: `company/taskBuilding/taskInsights`,
        method: 'GET',
        params: params,
      }),
      providesTags: () => ['Task'],
    }),
    getTeamTaskInsights: builder.query<
      Response<TaskInsights>,
      { phaseId?: number; projectId?: number }
    >({
      query: params => ({
        url: `company/taskBuilding/teamTaskInsights`,
        method: 'GET',
        params: params,
      }),
      providesTags: () => ['Task'],
    }),
    addUserFilterPref: builder.mutation<
      Response<{ filterPreference: string; filterPreferenceId: number }>,
      { filterPreference: string; filterPreferenceId: null }
    >({
      query: data => ({
        url: `company/userFilterPreference`,
        method: 'POST',
        body: data,
      }),
    }),
    getUserFilterPref: builder.query<
      Response<
        { filterPreference: string; id: number; createdAt: null | string }[]
      >,
      undefined
    >({
      query: () => ({
        url: `company/userFilterPreference`,
        method: 'GET',
      }),
    }),
    deleteUserFilterPref: builder.mutation<
      Response<null>,
      { filterPrefId: number }
    >({
      query: ({ filterPrefId }) => ({
        url: `company/userFilterPreference/${filterPrefId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetAllMyTasksListQuery,
  useExportAllMyTasksListMutation,
  useGetAllTeamTasksListQuery,
  useGetTaskByIdQuery,
  useCreateTaskMutation,
  useSubmitTaskMutation,
  useRejectTaskMutation,
  useGetTaskEventLogsQuery,
  useGetTaskDocumentsQuery,
  useUploadTaskDocumentsMutation,
  useGetTaskThreadsQuery,
  useCreateTaskCommentMutation,
  useGetMyTaskInsightsQuery,
  useGetTeamTaskInsightsQuery,
  useAddUserFilterPrefMutation,
  useGetUserFilterPrefQuery,
  useDeleteUserFilterPrefMutation,
} = tasksApiSlice;
